import { memo, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, RotateCw } from "lucide-react";

/**
 * Conteneur plein écran réutilisable pour les composants INTERACTIFS de passation
 * (placer des points, programme de construction…) — surtout pour le confort tactile mobile.
 *
 * - Porté sur document.body via createPortal → sort du `transform` de la carte blanche
 *   du quiz (sinon un `fixed` resterait piégé dans la carte).
 * - Affiche `children` en grand : la MÊME instance de composant est relocalisée ici,
 *   donc l'état/la réponse sont préservés (rien n'est recalculé).
 *
 * @param {boolean} open
 * @param {Function} onClose
 * @param {string} title
 * @param {React.ReactNode} children
 */
const PleinEcranInteractif = memo(function PleinEcranInteractif({ open, onClose, title, children }) {
    // Bloque le scroll du body tant que le plein écran est ouvert
    useEffect(() => {
        if (!open) return undefined;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10020] flex flex-col bg-slate-950/97 backdrop-blur-md">
            {/* En-tête fin */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-800">
                <p className="text-white font-bold text-sm sm:text-base truncate" style={{ fontFamily: 'Fredoka' }}>
                    {title}
                </p>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fermer"
                    className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Astuce paysage (portrait étroit uniquement) — non bloquante */}
            <p className="hidden max-[640px]:portrait:flex items-center justify-center gap-2 px-4 py-1.5 text-[11px] text-slate-400 bg-slate-900/60">
                <RotateCw className="w-3.5 h-3.5" /> Tourne ton téléphone pour plus de place
            </p>

            {/* Corps */}
            <div className="flex-1 overflow-auto px-3 sm:px-5 py-4">
                {children}
            </div>
        </div>,
        document.body
    );
});

export default PleinEcranInteractif;
